import React, { useEffect, useState, useContext, ChangeEvent } from 'react';
import InputMask from 'react-input-mask';
import CurrencyInput from '~/components/Modules/CurrencyMask';
import './styles.css';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

// Contexts
import { GeneralContext, ModalContext } from '~/contexts';
import { UnitsContext } from '~/contexts/forms/units';
import funcs from '~/utils/funcs';
import api from '~/services/api';

// Função para tratar data do contrato
function treatDateContract(date: string) {
    if (!date) return '';
    let contractDate = new Date(date);
    let month = contractDate.getUTCMonth() + 1;
    let year = contractDate.getUTCFullYear();
    return (month < 10 ? '0' : '') + month + '/' + year;
}

function normalizeProviderCode(value: any) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function getEnergyUtilityCode(el: any) {
    return el?.agentCode ?? el?.agentProfileCode ?? el?.code ?? el?.id ?? '';
}

function getEnergyUtilityName(el: any) {
    return el?.agentAcronym || el?.agentProfileAcronym || el?.legalName || el?.cnpj || el?.publicId || el?.id || '';
}

const Form: React.FC<any> = ({ unit, groups, providers, setReady}) => {
    const [unitData, setUnitData] = useState<any>(null);
    const [content, setContent]   = useState<string>('unit');
    const [energyUtilities, setEnergyUtilities] = useState<any>([]);
    const [providerCodeOpen, setProviderCodeOpen] = useState<boolean>(false);
    const [providerCodeQuery, setProviderCodeQuery] = useState<string>('');
    const [providerCodeHighlight, setProviderCodeHighlight] = useState<number>(0);
    const [identificationFormat, setIdentificationFormat] = useState<'old' | 'new'>('old');
    const [identificationFormatTouched, setIdentificationFormatTouched] = useState<boolean>(false);
    const {user} = useContext(GeneralContext);

    // Contexts
    const Units = useContext(UnitsContext);
    const Modal = useContext(ModalContext);

    useEffect(() => {
        if (!user?.token) return;
        api.get(`/energy_utility/`, {
            headers: { Authorization: user.token }
        }).then((resp: any) => {
            const list = resp?.data?.data;
            setEnergyUtilities(Array.isArray(list) ? list : []);
        }).catch(err => {
            console.error("🚨 ~ API Error:", err);
            setEnergyUtilities([]);
        });
    }, [user?.token]);

    useEffect(() => {
        if (unit === null) {
            setUnitData({
                id: 0,
                client_id: 0,
                client_type: 'serena',
                init_contract: '',
                name: '',
                cnpj: '',
                cnpj_matriz: null,
                agent: 0,
                identification_code: '',
                city: '',
                state: '',
                address: '',
                cost_reference: 0,
                active: true,
                contract_price_type: 'fixed',
                unit_type: 'retailer',
                contracted_demand_off_peak: 0,
                contracted_demand_peak: 0,
                icms: 0,
                lost_energy: null,
                percentage_consumption_peak: 0,
                contract_economy: 0,
                pis_cofins: 0,
                provider: {
                    provider_id: 0,
                    energy_utility_code: null,
                    type: 'conv',
                    modality: 'Azul',
                    power: 'A3'
                }
            });
        } else {
            api.get(`/clients/view/unit/${unit.id}`, {
            headers: { Authorization: user.token }
            }).then((resp: any) => {
                
                const rawData = resp.data;
                
                // Processar e normalizar os dados
                const processedData = {
                    ...rawData,
                    // Tratar data do contrato
                    init_contract: rawData.init_contract ? treatDateContract(rawData.init_contract) : '',
                    
                // Normalizar contract_price_type
                contract_price_type: rawData.contract_price_type || 'fixed',
                
                // Normalizar unit_type
                unit_type: rawData.unit_type || 'retailer',
                
                // Extrair valores de arrays
                icms: (Array.isArray(rawData.icms) && rawData.icms.length > 0) 
                    ? rawData.icms[0].value 
                    : (typeof rawData.icms === 'number' ? rawData.icms : 0),
                    
                lost_energy: (Array.isArray(rawData.lost_energy) && rawData.lost_energy.length > 0) 
                    ? rawData.lost_energy[0].value 
                    : (typeof rawData.lost_energy === 'number' ? rawData.lost_energy : null),

                pis_cofins: (Array.isArray(rawData.pis_cofins) && rawData.pis_cofins.length > 0) 
                    ? rawData.pis_cofins[0].value 
                    : (typeof rawData.pis_cofins === 'number' ? rawData.pis_cofins : 0),
                
                contract_economy: rawData.contract_economy || 0,
                
                // Garantir valores numéricos
                percentage_consumption_peak: rawData.percentage_consumption_peak || 0,
                contracted_demand_peak: rawData.contracted_demand_peak || 0,
                contracted_demand_off_peak: rawData.contracted_demand_off_peak || 0,
                
                // Normalizar provider
                provider: rawData.provider ? {
                    provider_id: rawData.provider.provider?.id || rawData.provider.provider_id || 0,
                    energy_utility_code: normalizeProviderCode(rawData.provider.energy_utility_code ?? rawData.provider.providerCode),
                    type: rawData.provider.type || 'conv',
                    modality: rawData.provider.modality || 'Azul',
                    power: rawData.provider.power || 'A3'
                } : {
                    provider_id: 0,
                    energy_utility_code: null,
                    type: 'conv',
                    modality: 'Azul',
                    power: 'A3'
                }
            };
            
            setUnitData(processedData);
        }).catch(err => {
            console.error("🚨 ~ API Error:", err);
            const { status } = err.response;

            if (status === 400) {
                const error = err.response.data.errors;
                console.log(error); 
            }
        });
    }
    }, [unit, user?.token]);

    function changeTab(tab: string) {
        if (tab!==content) setContent(tab)
    }

    function onChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
        const {classList, name, value} = event.target;
        const upper = (classList.contains('upC') ? true : false);
        let newData = JSON.parse(JSON.stringify(unitData))
        let data    = funcs.onChange(newData, name, value, upper);
        setUnitData(data);
    }

    function onChangeFloat(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>, originalValue: any) {
        let {name}  = event.target;
        let newData = JSON.parse(JSON.stringify(unitData))
        let data    = funcs.onChangeFloat(newData, name, originalValue);
        setUnitData(data);
    }

    function setProviderCodeValue(code: any) {
        let newData = JSON.parse(JSON.stringify(unitData));
        const numeric = code === null || code === undefined || code === '' ? null : Number(code);
        let data = funcs.onChangeFloat(newData, 'provider.energy_utility_code', numeric as any);
        setUnitData(data);
    }

    useEffect(() => {
        if (identificationFormatTouched) return;
        const code = unitData?.provider?.energy_utility_code;
        const isNew = code !== null && code !== undefined && code !== '';
        setIdentificationFormat(isNew ? 'new' : 'old');
    }, [unitData?.provider?.energy_utility_code, identificationFormatTouched]);

    const filteredEnergyUtilities =
        providerCodeQuery.trim() === ''
            ? energyUtilities
            : energyUtilities.filter((el: any) => {
                const code = String(getEnergyUtilityCode(el) ?? '');
                const name = String(getEnergyUtilityName(el) ?? '');
                const q = providerCodeQuery.trim().toLowerCase();
                return code.toLowerCase().includes(q) || name.toLowerCase().includes(q);
            });

    const limitedEnergyUtilities = Array.isArray(filteredEnergyUtilities)
        ? filteredEnergyUtilities.slice(0, 150)
        : [];
    const maxProviderCodeHighlight = limitedEnergyUtilities.length; // 0 = "Nenhuma"

    function selectHighlightedProviderCode() {
        if (providerCodeHighlight === 0) {
            setProviderCodeValue(null);
        } else {
            const el = limitedEnergyUtilities?.[providerCodeHighlight - 1];
            if (!el) return;
            const code = getEnergyUtilityCode(el);
            setProviderCodeValue(code);
        }
        setProviderCodeOpen(false);
        setProviderCodeQuery('');
    }

    if (!unitData) {
        return <div>Carregando dados da unidade...</div>;
    }

    // Verificação de segurança para evitar erros
    if (!unitData.provider) {
        console.warn("⚠️ ~ unitData.provider is missing, setting default");
        unitData.provider = {
            provider_id: 0,
            energy_utility_code: null,
            type: 'conv',
            modality: 'Azul',
            power: 'A3'
        };
    }
        
        return (
            <>
                <div className="tabs">
                    <div className={ `tab-unit${content==='unit' ? ' active' : ''}` } onClick={() => changeTab('unit') }>Unidade</div>
                    <div className={ `tab-provider${content==='provider' ? ' active' : ''}` } onClick={() => changeTab('provider') }>Dados adicionais</div>
                </div>

            <div className="contents">
                <div className={ `unit${content==='unit' ? ' active' : ''}` }>
                    <form method="post" onSubmit={(event: React.FormEvent<HTMLFormElement>) => event.preventDefault()} className="editClient">
                        <div className="form-group">
                            <div className="input-group">
                                <label htmlFor="client">Cliente</label>

                                <select
                                    name="client_id" id="client" onChange={onChange}
                                    defaultValue={unitData.client_id ? unitData.client_id : ''}
                                >
                                    <option value="">Nenhum</option>

                                    {
                                        groups && groups.length > 0 ? groups.map((el: any) => {
                                            return <option key={`units_${el.id}`} value={el.id}>{ el.name }</option>
                                        }) : <option disabled>Nenhum grupo disponível</option>
                                    }
                                </select>
                            </div>

                            <div className="input-group w250">
                                <label htmlFor="proinfa_code">Contrato Proinfa</label>
                                <InputMask 
                                    id="proinfa_code" 
                                    className={unitData.errors && unitData.errors.proinfa_code ? 'error' : ''} 
                                    name="proinfa_code" 
                                    type="text" 
                                    placeholder="Contrato Proinfa" 
                                    mask="9999999999" 
                                    maskChar={null} 
                                    value={unitData.proinfa_code ? String(unitData.proinfa_code).replace('.', ',') : ''} 
                                    onChange={onChange} 
                                />
                            </div>

                            <div className="input-group w200">
                                <label htmlFor="init_contract">Início <i>(01/2020)</i></label>
                                <InputMask id="init_contract" className={unitData.errors && unitData.errors.init_contract ? 'error' : ''} name="init_contract" type="text" placeholder="Início Contrato" mask="99/9999" maskChar={null} value={unitData.init_contract || ''} onChange={onChange} />
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="input-group">
                                <label htmlFor="name">Nome</label>
                                <input id="name" className={unitData.errors && unitData.errors.name ? 'error' : ''} name="name" type="text" placeholder="Nome" value={unitData.name || ''} onChange={onChange} />
                            </div>

                            <div className="input-group w220">
                                <label htmlFor="cost_reference">Custo Referência</label>

                                <CurrencyInput
                                    id="cost_reference" name="cost_reference" placeholder="0,00"
                                    onChangeValue={onChangeFloat}
                                    value={unitData.cost_reference ? unitData.cost_reference : ''}
                                    hideSymbol
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="input-group w200">
                                <label htmlFor="client_type">Cliente ({unitData.client_type})</label>
                                <select
                                    name="client_type" id="client_type"
                                    className={unitData.errors && unitData.errors.client_type ? 'error' : ''}
                                    onChange={onChange} defaultValue={unitData.client_type ? unitData.client_type : ''}
                                >
                                    <option value="energizou">Energizou</option>
                                    <option value="serena">Serena</option>
                                </select>
                            </div>

                            <div className="input-group w250">
                                <label htmlFor="cnpj">CNPJ</label>
                                <InputMask id="cnpj" className={unitData.errors && unitData.errors.cnpj ? 'error' : ''} name="cnpj" type="text" placeholder="CNPJ" mask="99.999.999/9999-99" maskChar={null} value={unitData.cnpj} onChange={onChange} />
                            </div>

                            <div className="input-group w180">
                                <label htmlFor="cnpj_matriz">CNPJ Matriz (Opcional)</label>
                                <InputMask id="cnpj_matriz" className={unitData.errors && unitData.errors.cnpj_matriz ? 'error' : ''} name="cnpj_matriz" type="text" placeholder="CNPJ" mask="99.999.999/9999-99" maskChar={null} value={unitData.cnpj_matriz ? unitData.cnpj_matriz : ''} onChange={onChange} />
                            </div>

                        </div>

                        <div className="form-group">
                        <div className="input-group w180">
                                <label htmlFor="identification_code">Código de Identificação</label>
                                <div style={{ margin: '6px 0 8px' }}>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={identificationFormat}
                                        onChange={(_, next) => {
                                            if (!next) return;
                                            setIdentificationFormatTouched(true);
                                            setIdentificationFormat(next);
                                            if (next === 'old') {
                                                // Old format doesn't use distributor code
                                                setProviderCodeOpen(false);
                                                setProviderCodeQuery('');
                                                setProviderCodeHighlight(0);
                                                setProviderCodeValue(null);
                                            }
                                        }}
                                        sx={{
                                            height: 30,
                                            '& .MuiToggleButton-root': {
                                                textTransform: 'none',
                                                fontSize: '0.8rem',
                                                padding: '3px 10px',
                                                lineHeight: 1.2,
                                            },
                                            '& .toggleNew.Mui-selected': {
                                                backgroundColor: '#ff695e',
                                                borderColor: '#ff392a',
                                                color: '#fff',
                                            },
                                            '& .toggleNew.Mui-selected:hover': {
                                                backgroundColor: '#ff5a4e',
                                            },
                                        }}
                                    >
                                        <ToggleButton value="old">Antigo</ToggleButton>
                                        <ToggleButton value="new" className="toggleNew">Novo</ToggleButton>
                                    </ToggleButtonGroup>
                                </div>
                                <input
                                    id="identification_code"
                                    className={`${unitData.errors && unitData.errors.identification_code ? 'error' : ''}${identificationFormat === 'new' ? ' coralBorder' : ''}`}
                                    name="identification_code"
                                    type="text"
                                    placeholder="UC ou Medidor"
                                    value={unitData.identification_code}
                                    onChange={onChange}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="input-group w220">
                                <label htmlFor="agent">Perfil ML</label>
                                <InputMask id="agent" className={unitData.errors && unitData.errors.agent ? 'error' : ''} name="agent" type="text" placeholder="Perfil ML" mask="999999" maskChar={null} value={unitData.agent ? String(unitData.agent).replace('.', ',') : ''} onChange={onChange} />
                            </div>

                            <div className="input-group w220">
                                <label htmlFor="profile">Agente</label>
                                <InputMask id="profile" className={unitData.errors && unitData.errors.profile ? 'error' : ''} name="profile" type="text" placeholder="Agente" mask="999999" maskChar={null} value={unitData.profile ? String(unitData.profile).replace('.', ',') : ''} onChange={onChange} />
                            </div>

                            <div className="input-group w140">
                                <label htmlFor="active">Status</label>
                                <select
                                    name="active" id="active"
                                    className={unitData.errors && unitData.errors.active ? 'error' : ''}
                                    onChange={onChange} defaultValue={unitData.active ? "1" : "0"}
                                >
                                    <option value="1">Ativo</option>
                                    <option value="0">Inativo</option>
                                </select>
                            </div>
                        </div>

                     <div className="form-group">
                        <div className="input-group">
                                <label htmlFor="contract_price_type">Tipo do Preço Contratado</label>
                                <select
                                    name="contract_price_type" id="contract_price_type"
                                    className={unitData.errors && unitData.errors.contract_price_type ? 'error' : ''}
                                    onChange={onChange}
                                    value={unitData.contract_price_type || 'fixed'}
                                >
                                    <option value="fixed">Fixo</option>
                                    <option value="guaranteedDiscount">Desconto garantido</option>
                                </select>
                            </div>
                            <div className="input-group">
                                <label htmlFor="unit_type">Tipo da Unidade</label>
                                <select
                                    name="unit_type" id="unit_type"
                                    className={unitData.errors && unitData.errors.unit_type ? 'error' : ''}
                                    onChange={onChange}
                                    value={unitData.unit_type || 'retailer'}
                                >
                                    <option value="retailer">Varejista</option>
                                    <option value="wholesaler">Atacadista</option>
                                </select>
                            </div>
                            {unitData.contract_price_type === 'guaranteedDiscount' && (
                            <div className="input-group">
                                <label htmlFor="contract_economy">Economia Contratada (%)</label>
                                <CurrencyInput
                                    id="contract_economy" name="contract_economy" placeholder="0  "
                                    onChangeValue={onChangeFloat}
                                    value={unitData.contract_economy ? unitData.contract_economy : ''}
                                    hideSymbol={true}
                                    digits={2}
                                />
                            </div>
                            )}
                        </div>
                        <hr />

                        <div className="input-group">
                            <label htmlFor="address">Endereço</label>
                                <input id="address" className={unitData.errors && unitData.errors.address ? 'error' : ''} name="address" type="text" placeholder="Endereço" value={unitData.address || ''} onChange={onChange} />
                        </div>

                        <div className="form-group">
                            <div className="input-group">
                                <label htmlFor="city">Cidade</label>
                                <input id="city" className={unitData.errors && unitData.errors.city ? 'error' : ''} name="city" type="text" placeholder="Cidade" value={unitData.city || ''} onChange={onChange} />
                            </div>

                            <div className="input-group w140">
                                <label htmlFor="state">Estado</label>
                                <InputMask id="state" className={unitData.errors && unitData.errors.state ? 'upC error' : 'upC'} name="state" type="text" placeholder="Estado" mask="aa" maskChar={null} value={unitData.state || ''} onChange={onChange} />
                            </div>
                        </div>
                    </form>
                </div>

                <div className={ `provider${content==='provider' ? ' active' : ''}` }>
                    <form method="post" onSubmit={(event: React.FormEvent<HTMLFormElement>) => event.preventDefault()} className="editClient">
                        
                        {/* Seção Distribuidora */}
                        <div className="section-header">
                            <h3>Distribuidora</h3>
                        </div>
                        
                        <div className="form-group">
                            <div className="input-group">
                                <label htmlFor="provider">Distribuidora</label>
                                <select
                                    name="provider.provider_id" id="provider"
                                    className={unitData.errors && unitData.errors['provider.provider_id'] ? 'error' : ''}
                                    onChange={onChange} defaultValue={unitData.provider?.provider_id ? unitData.provider.provider_id : '0'}
                                >
                                    <option value="0">Selecione a distribuidora</option>

                                    <optgroup label="Distribuidoras">
                                        {
                                            providers && providers.length > 0 ? providers.map((el: any) => (
                                                <option key={`providers_${el.id}`} value={el.id}>{ el.name }</option>
                                            )) : <option disabled>Nenhuma distribuidora disponível</option>
                                        }
                                    </optgroup>
                                </select>
                            </div>
                            {identificationFormat === 'new' && (
                                <div className="input-group w250 searchableSelect">
                                    <label htmlFor="energy_utility_code">Código da distribuidora</label>
                                    <input
                                        id="energy_utility_code"
                                        name="provider.energy_utility_code"
                                            className={`${unitData.errors && unitData.errors['provider.energy_utility_code'] ? 'error' : ''} coralBorder`}
                                        type="text"
                                        placeholder={providerCodeOpen ? 'Buscar por sigla ou código...' : 'Selecione o código'}
                                        value={
                                            providerCodeOpen
                                                ? providerCodeQuery
                                                : String(unitData.provider?.energy_utility_code ?? '')
                                        }
                                        onFocus={() => {
                                            setProviderCodeOpen(true);
                                            setProviderCodeHighlight(0);
                                        }}
                                        onBlur={() => {
                                            // small delay so click on option can register before closing
                                            setTimeout(() => {
                                                setProviderCodeOpen(false);
                                                setProviderCodeQuery('');
                                            }, 120);
                                        }}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            setProviderCodeQuery(e.target.value);
                                            setProviderCodeHighlight(0);
                                            if (!providerCodeOpen) setProviderCodeOpen(true);
                                        }}
                                        onKeyDown={(e: any) => {
                                            if (!providerCodeOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
                                                setProviderCodeOpen(true);
                                                return;
                                            }
                                            if (e.key === 'Escape') {
                                                setProviderCodeOpen(false);
                                                setProviderCodeQuery('');
                                                return;
                                            }
                                            if (e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                setProviderCodeHighlight((i: number) =>
                                                    Math.min(i + 1, Math.max(maxProviderCodeHighlight, 0))
                                                );
                                                return;
                                            }
                                            if (e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                setProviderCodeHighlight((i: number) => Math.max(i - 1, 0));
                                                return;
                                            }
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                selectHighlightedProviderCode();
                                            }
                                        }}
                                        autoComplete="off"
                                    />

                                    {providerCodeOpen && (
                                        <div className="searchableSelect__dropdown">
                                            <div
                                                className={`searchableSelect__option${providerCodeHighlight === 0 ? ' active' : ''}`}
                                                onMouseDown={(evt) => evt.preventDefault()}
                                                onClick={() => {
                                                    setProviderCodeValue(null);
                                                    setProviderCodeOpen(false);
                                                    setProviderCodeQuery('');
                                                }}
                                            >
                                                Nenhuma
                                            </div>

                                            {limitedEnergyUtilities.length > 0 ? (
                                                limitedEnergyUtilities.map((el: any, idx: number) => {
                                                    const code = getEnergyUtilityCode(el);
                                                    const name = getEnergyUtilityName(el);
                                                    const active = (idx + 1) === providerCodeHighlight;
                                                    return (
                                                        <div
                                                            key={`energy_utilities_opt_${el.publicId || el.id}`}
                                                            className={`searchableSelect__option${active ? ' active' : ''}`}
                                                            onMouseDown={(evt) => evt.preventDefault()}
                                                            onClick={() => {
                                                                setProviderCodeValue(code);
                                                                setProviderCodeOpen(false);
                                                                setProviderCodeQuery('');
                                                            }}
                                                        >
                                                            {`${name} : ${code}`}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="searchableSelect__empty">Nenhuma distribuidora encontrada</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <div className="input-group">
                                <label htmlFor="type">Tipo</label>
                                <select
                                    name="provider.type" id="type"
                                    className={unitData.errors && unitData.errors['provider.type'] ? 'error' : ''}
                                    onChange={onChange} defaultValue={unitData.provider?.type || 'conv'}
                                >
                                    <option value="conv">Convencional</option>
                                    <option value="inc">Incentivada</option>
                                </select>
                            </div>

                            <div className="input-group">
                                <label htmlFor="modality">Modalidade</label>
                                <select
                                    name="provider.modality" id="modality"
                                    className={unitData.errors && unitData.errors['provider.modality'] ? 'error' : ''}
                                    onChange={onChange}
                                    defaultValue={unitData.provider?.modality || 'Azul'}
                                >
                                    <option value="Azul">Azul</option>
                                    <option value="Verde">Verde</option>
                                </select>
                            </div>

                            <div className="input-group">
                                <label htmlFor="power">Energia</label>
                                <select
                                    name="provider.power" id="power"
                                    className={unitData.errors && unitData.errors['provider.power'] ? 'error' : ''}
                                    onChange={onChange}
                                    defaultValue={unitData.provider?.power || 'A3'}
                                >
                                    <option value="A3">A3</option>
                                    <option value="A4">A4</option>
                                    <option value="AS">AS</option>
                                </select>
                            </div>
                        </div>

                        {/* Seção Dados Adicionais */}
                        <div className="section-header">
                            <h3>Dados adicionais</h3>
                        </div>

                        <div className="form-group">
                            <div className="input-group w150">
                                <label htmlFor="lost_energy">Perdas (%)</label>
                                <CurrencyInput
                                    id="lost_energy" name="lost_energy" placeholder="0,00"
                                    onChangeValue={onChangeFloat}
                                    value={unitData.lost_energy ? unitData.lost_energy : ''}
                                    hideSymbol
                                />
                            </div>

                            <div className="input-group w150">
                                <label htmlFor="icms">ICMS (%)</label>
                                <CurrencyInput
                                    id="icms" name="icms" placeholder="0,00"
                                    onChangeValue={onChangeFloat}
                                    value={unitData.icms ? unitData.icms : ''}
                                    hideSymbol
                                />
                            </div>

                            <div className="input-group w150">
                                <label htmlFor="percentage_consumption_peak">Percentual Ponta (%)</label>
                                <CurrencyInput
                                    id="percentage_consumption_peak" name="percentage_consumption_peak" placeholder="0,00"
                                    onChangeValue={onChangeFloat}
                                    value={unitData.percentage_consumption_peak ? unitData.percentage_consumption_peak : ''}
                                    hideSymbol
                                />
                            </div>
                            <div className="input-group w100">
                                <label htmlFor="pis_cofins">PIS/COFINS (%)</label>
                                <CurrencyInput
                                    id="pis_cofins" name="pis_cofins" placeholder="0,00"
                                    onChangeValue={onChangeFloat}
                                    value={unitData.pis_cofins ? unitData.pis_cofins : ''}
                                    hideSymbol
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            {unitData.provider?.modality !== 'Verde' ? (
                                <div className="input-group w250">
                                    <label htmlFor="contracted_demand_peak">Demanda Contratada Ponta (kW)</label>
                                    <CurrencyInput
                                        id="contracted_demand_peak" name="contracted_demand_peak" placeholder="0,00"
                                        onChangeValue={onChangeFloat}
                                        value={unitData.contracted_demand_peak ? unitData.contracted_demand_peak : ''}
                                        hideSymbol
                                    />
                                </div>
                            ) : <>  </>
                        }
                            <div className="input-group w250">
                                <label htmlFor="contracted_demand_off_peak">Demanda Contratada Fora Ponta (kW)</label>
                                <CurrencyInput
                                    id="contracted_demand_off_peak" name="contracted_demand_off_peak" placeholder="0,00"
                                    onChangeValue={onChangeFloat}
                                    value={unitData.contracted_demand_off_peak ? unitData.contracted_demand_off_peak : ''}
                                    hideSymbol
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </div>

                            <footer>
                <button id="btnSave" type="button" onClick={() => Units.save(unitData, Modal, setReady)}>Salvar</button>
                <button id="btnCancel" type="button" onClick={() => Modal.setModalOpen(false)}>Cancelar</button>
            </footer>
        </>
        );
}

export default Form;